import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DashboardLayout from '../layout/DashboardLayout';
import DashboardHeader from '../dashboard/DashboardHeader';
import { config } from '../../config/config';
import { fetchLeads } from '../../store/slices/leadsSlice';
import { fetchProjects } from '../../store/slices/projectsSlice';
import {
  FiSearch,
  FiTrash2,
  FiMap,
  FiRotateCcw,
  FiInfo,
  FiUsers,
  FiClipboard,
  FiMapPin,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiPrinter,
  FiEdit2,
  FiX,
  FiCheck,
  FiCompass,
  FiZap,
  FiHexagon,
  FiHash,
  FiNavigation,
  FiCrosshair
} from 'react-icons/fi';
import Spinner from '../common/Spinner';

// Helper function to calculate distance between two points in feet
const calculateDistance = (coord1, coord2) => {
  const point1 = turf.point(coord1);
  const point2 = turf.point(coord2);
  return turf.distance(point1, point2, { units: 'feet' });
};

// Helper to get edge measurements for a polygon
const getEdgeMeasurements = (coordinates) => {
  const edges = [];
  const coords = coordinates[0]; // Get the first ring of the polygon
  
  for (let i = 0; i < coords.length - 1; i++) {
    const start = coords[i];
    const end = coords[i + 1];
    const distance = calculateDistance(start, end);
    const midpoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
    
    edges.push({
      start,
      end,
      midpoint,
      distance: Math.round(distance)
    });
  }
  
  return edges;
};

// Set mapbox token
mapboxgl.accessToken = config.mapboxAccessToken;

// Pitch multipliers for actual roof area calculation
const PITCH_MULTIPLIERS = {
  '0': 1.000,
  '1': 1.003,
  '2': 1.014,
  '3': 1.031,
  '4': 1.054,
  '5': 1.083,
  '6': 1.118,
  '7': 1.158,
  '8': 1.202,
  '9': 1.250,
  '10': 1.302,
  '11': 1.357,
  '12': 1.414,
  '13': 1.474,
  '14': 1.537,
  '15': 1.601,
  '16': 1.667,
  '17': 1.734,
  '18': 1.803
};

// Waste factors based on roof complexity
const WASTE_FACTORS = [
  { label: '0%', value: 0, description: 'Simple roof' },
  { label: '10%', value: 0.10, description: 'Low complexity' },
  { label: '15%', value: 0.15, description: 'Medium complexity' },
  { label: '20%', value: 0.20, description: 'Standard (recommended)' },
  { label: '22%', value: 0.22, description: 'Above average' },
  { label: '25%', value: 0.25, description: 'High complexity' },
  { label: '30%', value: 0.30, description: 'Very complex' },
];

// Section types for labeling
const SECTION_TYPES = [
  { id: 'main', label: 'Main Roof', color: '#3b82f6' },
  { id: 'garage', label: 'Garage', color: '#22c55e' },
  { id: 'addition', label: 'Addition', color: '#f59e0b' },
  { id: 'porch', label: 'Porch/Patio', color: '#8b5cf6' },
  { id: 'dormer', label: 'Dormer', color: '#ec4899' },
  { id: 'other', label: 'Other', color: '#6b7280' },
];

// Line types for roof features (like GAF QuickMeasure)
const LINE_TYPES = [
  { id: 'ridge', label: 'Ridge', color: '#dc2626', description: 'Peak of roof' },
  { id: 'hip', label: 'Hip', color: '#dc2626', description: 'Sloped ridge' },
  { id: 'valley', label: 'Valley', color: '#7c3aed', description: 'Inside corner' },
  { id: 'rake', label: 'Rake', color: '#22c55e', description: 'Sloped edge' },
  { id: 'eave', label: 'Eave', color: '#6b7280', description: 'Bottom edge' },
  { id: 'drip', label: 'Drip Edge', color: '#0ea5e9', description: 'Eave + Rake' },
  { id: 'flash', label: 'Flashing', color: '#f59e0b', description: 'Wall junction' },
  { id: 'step', label: 'Step Flash', color: '#ec4899', description: 'Stepped flash' },
];

// Line types that should render as repeating "X" symbols on the map and in the
// printable diagram instead of a solid stroked line.
const FLASHING_LINE_TYPES = ['flash', 'step'];
const isFlashingLineType = (type) => FLASHING_LINE_TYPES.includes(type);

// Walk an SVG-pixel polyline and produce evenly spaced positions for X markers.
const computeXMarkerPositions = (pathPoints, spacingPx = 18) => {
  const xs = [];
  if (!pathPoints || pathPoints.length < 2) return xs;
  let carry = spacingPx / 2; // first X starts half-spacing in so it isn't at an endpoint
  for (let i = 0; i < pathPoints.length - 1; i++) {
    const [x1, y1] = pathPoints[i];
    const [x2, y2] = pathPoints[i + 1];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segLen = Math.hypot(dx, dy);
    if (segLen === 0) continue;
    const ux = dx / segLen;
    const uy = dy / segLen;
    let pos = carry;
    while (pos < segLen) {
      xs.push({ x: x1 + ux * pos, y: y1 + uy * pos });
      pos += spacingPx;
    }
    carry = pos - segLen;
  }
  return xs;
};

// Roof Diagram SVG Component
const RoofDiagram = ({ measurements, roofLines = [], showMeasurements = true, width = 600, height = 450 }) => {
  if ((!measurements || measurements.length === 0) && (!roofLines || roofLines.length === 0)) return null;

  // Get all coordinates to calculate bounds (including lines)
  const polygonCoords = measurements.flatMap(m => m.coordinates[0]);
  const lineCoords = roofLines.flatMap(l => l.coordinates);
  const allCoords = [...polygonCoords, ...lineCoords];
  
  if (allCoords.length === 0) return null;
  
  const lngs = allCoords.map(c => c[0]);
  const lats = allCoords.map(c => c[1]);
  
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  
  // Calculate center of all shapes
  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;
  
  const padding = 80;
  const legendWidth = 130;
  const diagramWidth = width - padding * 2 - legendWidth;
  const diagramHeight = height - padding * 2;
  
  // Scale factors - ensure shapes fit in the diagram area
  const lngRange = maxLng - minLng || 0.0001;
  const latRange = maxLat - minLat || 0.0001;
  
  // Use a uniform scale to maintain aspect ratio
  const scaleX = diagramWidth / lngRange;
  const scaleY = diagramHeight / latRange;
  const scale = Math.min(scaleX, scaleY) * 0.85; // 85% to add some margin
  
  // Calculate offset to center the diagram
  const scaledWidth = lngRange * scale;
  const scaledHeight = latRange * scale;
  const offsetX = padding + (diagramWidth - scaledWidth) / 2;
  const offsetY = padding + (diagramHeight - scaledHeight) / 2;
  
  // Convert geo coordinates to SVG coordinates (centered)
  const toSvg = (coord) => {
    const x = offsetX + (coord[0] - minLng) * scale;
    const y = offsetY + (maxLat - coord[1]) * scale; // Flip Y axis
    return [x, y];
  };

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="bg-white border border-gray-200 rounded-lg" style={{ maxWidth: '100%' }}>
      {/* Background */}
      <rect x="0" y="0" width={width} height={height} fill="#fafafa" rx="8" />
      
      {/* North Arrow */}
      <g transform={`translate(40, 50)`}>
        <circle cx="0" cy="0" r="22" fill="white" stroke="#d1d5db" strokeWidth="1" />
        <polygon points="0,-15 4,-5 0,-8 -4,-5" fill="#374151" />
        <line x1="0" y1="-5" x2="0" y2="12" stroke="#374151" strokeWidth="2" />
        <text x="0" y="-22" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#6b7280">N</text>
      </g>

      {/* Draw each section */}
      {measurements.map((section, sectionIndex) => {
        const sectionType = SECTION_TYPES.find(t => t.id === section.type);
        const color = sectionType?.color || '#3b82f6';
        const coords = section.coordinates[0];
        const edges = getEdgeMeasurements(section.coordinates);
        
        // Create path for polygon
        const pathPoints = coords.map(coord => toSvg(coord));
        const pathD = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ') + ' Z';
        
        // Calculate centroid for label
        const centroid = turf.centroid(turf.polygon(section.coordinates));
        const centroidSvg = toSvg(centroid.geometry.coordinates);

        return (
          <g key={section.id}>
            {/* Polygon fill */}
            <path
              d={pathD}
              fill={`${color}55`}
              stroke={color}
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            
            {/* Edge measurements */}
            {showMeasurements && edges.map((edge, edgeIndex) => {
              const startSvg = toSvg(edge.start);
              const endSvg = toSvg(edge.end);
              const midSvg = [(startSvg[0] + endSvg[0]) / 2, (startSvg[1] + endSvg[1]) / 2];
              
              // Calculate edge length in SVG units to determine if we show the label
              const edgeLengthSvg = Math.sqrt(
                Math.pow(endSvg[0] - startSvg[0], 2) + Math.pow(endSvg[1] - startSvg[1], 2)
              );
              
              // Only show measurements for edges that are long enough to display
              if (edge.distance < 5 || edgeLengthSvg < 30) return null;
              
              // Calculate angle for text rotation
              const angle = Math.atan2(endSvg[1] - startSvg[1], endSvg[0] - startSvg[0]) * 180 / Math.PI;
              const adjustedAngle = (angle > 90 || angle < -90) ? angle + 180 : angle;
              
              // Offset text perpendicular to edge (outside the polygon)
              const perpAngle = (angle + 90) * Math.PI / 180;
              const offset = 15;
              const textX = midSvg[0] + Math.cos(perpAngle) * offset;
              const textY = midSvg[1] + Math.sin(perpAngle) * offset;

              return (
                <g key={`${section.id}-edge-${edgeIndex}`}>
                  {/* Background for text */}
                  <rect
                    x={textX - 18}
                    y={textY - 8}
                    width="36"
                    height="16"
                    rx="3"
                    fill="white"
                    opacity="0.9"
                    transform={`rotate(${adjustedAngle}, ${textX}, ${textY})`}
                  />
                  {/* Measurement text */}
                  <text
                    x={textX}
                    y={textY + 1}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="11"
                    fontWeight="600"
                    fill="#374151"
                    transform={`rotate(${adjustedAngle}, ${textX}, ${textY})`}
                  >
                    {edge.distance}'
                  </text>
                </g>
              );
            })}
            
            {/* Section label with area */}
            <rect
              x={centroidSvg[0] - 40}
              y={centroidSvg[1] - 12}
              width="80"
              height="24"
              rx="6"
              fill={color}
              stroke="white"
              strokeWidth="2"
            />
            <text
              x={centroidSvg[0]}
              y={centroidSvg[1] + 5}
              textAnchor="middle"
              fontSize="12"
              fontWeight="700"
              fill="white"
            >
              {section.areaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} SF
            </text>
          </g>
        );
      })}

      {/* Draw roof lines */}
      {roofLines.map((line) => {
        const lineType = LINE_TYPES.find(t => t.id === line.type);
        const color = lineType?.color || '#6b7280';
        const coords = line.coordinates;
        const flashing = isFlashingLineType(line.type);
        
        // Create path for line
        const pathPoints = coords.map(coord => toSvg(coord));
        const pathD = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
        
        // Calculate midpoint for label
        const midIdx = Math.floor(coords.length / 2);
        const midCoord = coords[midIdx];
        const midSvg = toSvg(midCoord);

        const xMarkers = flashing ? computeXMarkerPositions(pathPoints, 16) : [];

        return (
          <g key={line.id}>
            {/* Line — solid for normal types, faint dashed for flashing so the X marks read as the primary symbol */}
            <path
              d={pathD}
              fill="none"
              stroke={color}
              strokeWidth={flashing ? 1 : 3}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={flashing ? '3 3' : undefined}
              opacity={flashing ? 0.55 : 1}
            />

            {/* X markers along flashing lines */}
            {flashing && xMarkers.map((pos, i) => (
              <text
                key={`x-${i}`}
                x={pos.x}
                y={pos.y + 4}
                textAnchor="middle"
                fontSize="13"
                fontWeight="800"
                fill={color}
                stroke="white"
                strokeWidth="2.5"
                paintOrder="stroke"
                style={{ userSelect: 'none' }}
              >
                X
              </text>
            ))}
            
            {/* Length label */}
            {showMeasurements && (
              <>
                <rect
                  x={midSvg[0] - 20}
                  y={midSvg[1] - 10}
                  width="40"
                  height="20"
                  rx="4"
                  fill={color}
                />
                <text
                  x={midSvg[0]}
                  y={midSvg[1] + 4}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="600"
                  fill="white"
                >
                  {Math.round(line.lengthFt)}'
                </text>
              </>
            )}
          </g>
        );
      })}

      {/* Legend */}
      <g transform={`translate(${width - legendWidth - 10}, 20)`}>
        <rect x="0" y="0" width={legendWidth} height={40 + measurements.length * 24} fill="white" stroke="#e5e7eb" strokeWidth="1" rx="6" />
        <text x="12" y="22" fontSize="11" fontWeight="700" fill="#374151">Sections</text>
        <line x1="12" y1="32" x2={legendWidth - 12} y2="32" stroke="#e5e7eb" strokeWidth="1" />
        {measurements.map((section, i) => {
          const sectionType = SECTION_TYPES.find(t => t.id === section.type);
          return (
            <g key={section.id} transform={`translate(12, ${42 + i * 24})`}>
              <rect x="0" y="0" width="16" height="16" fill={sectionType?.color || '#3b82f6'} rx="3" />
              <text x="22" y="12" fontSize="10" fill="#374151" fontWeight="500">{section.name}</text>
            </g>
          );
        })}
      </g>
      
      {/* Scale indicator */}
      <g transform={`translate(${width - legendWidth - 10}, ${height - 40})`}>
        <rect x="0" y="0" width={legendWidth} height="30" fill="white" stroke="#e5e7eb" strokeWidth="1" rx="6" />
        <text x={legendWidth / 2} y="18" textAnchor="middle" fontSize="10" fill="#6b7280">
          Total: {measurements.reduce((sum, m) => sum + m.areaSqFt, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} SF
        </text>
      </g>
    </svg>
  );
};

// Generate SVG string for print report
const generateDiagramSVG = (measurements, roofLines = [], width = 700, height = 500) => {
  if ((!measurements || measurements.length === 0) && (!roofLines || roofLines.length === 0)) return '';

  const polygonCoords = measurements.flatMap(m => m.coordinates[0]);
  const lineCoords = roofLines.flatMap(l => l.coordinates);
  const allCoords = [...polygonCoords, ...lineCoords];
  
  if (allCoords.length === 0) return '';
  const lngs = allCoords.map(c => c[0]);
  const lats = allCoords.map(c => c[1]);
  
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  
  const padding = 80;
  const legendWidth = 140;
  const diagramWidth = width - padding * 2 - legendWidth;
  const diagramHeight = height - padding * 2;
  
  const lngRange = maxLng - minLng || 0.0001;
  const latRange = maxLat - minLat || 0.0001;
  
  const scaleX = diagramWidth / lngRange;
  const scaleY = diagramHeight / latRange;
  const scale = Math.min(scaleX, scaleY) * 0.85;
  
  const scaledWidth = lngRange * scale;
  const scaledHeight = latRange * scale;
  const offsetX = padding + (diagramWidth - scaledWidth) / 2;
  const offsetY = padding + (diagramHeight - scaledHeight) / 2;
  
  const toSvg = (coord) => {
    const x = offsetX + (coord[0] - minLng) * scale;
    const y = offsetY + (maxLat - coord[1]) * scale;
    return [x, y];
  };

  let svgContent = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="background: #fafafa; border: 1px solid #e5e7eb; border-radius: 8px;">
      <!-- Background -->
      <rect x="0" y="0" width="${width}" height="${height}" fill="#fafafa" rx="8" />
      
      <!-- North Arrow -->
      <g transform="translate(45, 55)">
        <circle cx="0" cy="0" r="24" fill="white" stroke="#d1d5db" stroke-width="1" />
        <polygon points="0,-16 5,-6 0,-9 -5,-6" fill="#374151" />
        <line x1="0" y1="-6" x2="0" y2="14" stroke="#374151" stroke-width="2" />
        <text x="0" y="-24" text-anchor="middle" font-size="11" font-weight="bold" fill="#6b7280">N</text>
      </g>
  `;

  measurements.forEach((section) => {
    const sectionType = SECTION_TYPES.find(t => t.id === section.type);
    const color = sectionType?.color || '#3b82f6';
    const coords = section.coordinates[0];
    const edges = getEdgeMeasurements(section.coordinates);
    
    const pathPoints = coords.map(coord => toSvg(coord));
    const pathD = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ') + ' Z';
    
    const centroid = turf.centroid(turf.polygon(section.coordinates));
    const centroidSvg = toSvg(centroid.geometry.coordinates);

    svgContent += `
      <g>
        <path d="${pathD}" fill="${color}55" stroke="${color}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round" />
    `;

    edges.forEach((edge) => {
      const startSvg = toSvg(edge.start);
      const endSvg = toSvg(edge.end);
      
      const edgeLengthSvg = Math.sqrt(
        Math.pow(endSvg[0] - startSvg[0], 2) + Math.pow(endSvg[1] - startSvg[1], 2)
      );
      
      if (edge.distance < 5 || edgeLengthSvg < 35) return;
      
      const midSvg = [(startSvg[0] + endSvg[0]) / 2, (startSvg[1] + endSvg[1]) / 2];
      const angle = Math.atan2(endSvg[1] - startSvg[1], endSvg[0] - startSvg[0]) * 180 / Math.PI;
      const adjustedAngle = (angle > 90 || angle < -90) ? angle + 180 : angle;
      
      const perpAngle = (angle + 90) * Math.PI / 180;
      const offset = 16;
      const textX = midSvg[0] + Math.cos(perpAngle) * offset;
      const textY = midSvg[1] + Math.sin(perpAngle) * offset;

      svgContent += `
        <rect x="${textX - 20}" y="${textY - 9}" width="40" height="18" rx="4" fill="white" opacity="0.95" transform="rotate(${adjustedAngle}, ${textX}, ${textY})" />
        <text x="${textX}" y="${textY + 1}" text-anchor="middle" dominant-baseline="middle" font-size="12" font-weight="600" fill="#1f2937" transform="rotate(${adjustedAngle}, ${textX}, ${textY})">${edge.distance}'</text>
      `;
    });

    svgContent += `
        <rect x="${centroidSvg[0] - 45}" y="${centroidSvg[1] - 14}" width="90" height="28" rx="6" fill="${color}" stroke="white" stroke-width="2" />
        <text x="${centroidSvg[0]}" y="${centroidSvg[1] + 6}" text-anchor="middle" font-size="14" font-weight="700" fill="white">${section.areaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} SF</text>
      </g>
    `;
  });

  // Draw roof lines
  roofLines.forEach((line) => {
    const lineType = LINE_TYPES.find(t => t.id === line.type);
    const color = lineType?.color || '#6b7280';
    const coords = line.coordinates;
    const flashing = isFlashingLineType(line.type);

    const pathPoints = coords.map(coord => toSvg(coord));
    const pathD = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');

    const midIdx = Math.floor(coords.length / 2);
    const midCoord = coords[midIdx];
    const midSvg = toSvg(midCoord);

    const lineStrokeWidth = flashing ? 1 : 3;
    const lineDash = flashing ? ' stroke-dasharray="3 3" opacity="0.55"' : '';

    let xMarkersSvg = '';
    if (flashing) {
      const positions = computeXMarkerPositions(pathPoints, 18);
      xMarkersSvg = positions
        .map(
          (pos) =>
            `<text x="${pos.x}" y="${pos.y + 5}" text-anchor="middle" font-size="14" font-weight="800" fill="${color}" stroke="white" stroke-width="2.5" paint-order="stroke">X</text>`
        )
        .join('');
    }

    svgContent += `
      <g>
        <path d="${pathD}" fill="none" stroke="${color}" stroke-width="${lineStrokeWidth}" stroke-linecap="round" stroke-linejoin="round"${lineDash} />
        ${xMarkersSvg}
        <rect x="${midSvg[0] - 22}" y="${midSvg[1] - 11}" width="44" height="22" rx="4" fill="${color}" />
        <text x="${midSvg[0]}" y="${midSvg[1] + 5}" text-anchor="middle" font-size="11" font-weight="600" fill="white">${Math.round(line.lengthFt)}'</text>
      </g>
    `;
  });

  // Legend
  const legendHeight = 50 + measurements.length * 26;
  svgContent += `
    <g transform="translate(${width - legendWidth - 15}, 20)">
      <rect x="0" y="0" width="${legendWidth}" height="${legendHeight}" fill="white" stroke="#e5e7eb" stroke-width="1" rx="8" />
      <text x="14" y="24" font-size="12" font-weight="700" fill="#374151">Roof Sections</text>
      <line x1="14" y1="36" x2="${legendWidth - 14}" y2="36" stroke="#e5e7eb" stroke-width="1" />
  `;

  measurements.forEach((section, i) => {
    const sectionType = SECTION_TYPES.find(t => t.id === section.type);
    svgContent += `
      <g transform="translate(14, ${48 + i * 26})">
        <rect x="0" y="0" width="18" height="18" fill="${sectionType?.color || '#3b82f6'}" rx="4" />
        <text x="26" y="14" font-size="11" fill="#374151" font-weight="500">${section.name}</text>
      </g>
    `;
  });

  svgContent += `</g>`;

  // Total box
  const totalArea = measurements.reduce((sum, m) => sum + m.areaSqFt, 0);
  svgContent += `
    <g transform="translate(${width - legendWidth - 15}, ${height - 50})">
      <rect x="0" y="0" width="${legendWidth}" height="40" fill="white" stroke="#e5e7eb" stroke-width="1" rx="8" />
      <text x="${legendWidth / 2}" y="16" text-anchor="middle" font-size="10" fill="#6b7280">Total Area</text>
      <text x="${legendWidth / 2}" y="32" text-anchor="middle" font-size="14" font-weight="700" fill="#1f2937">${totalArea.toLocaleString(undefined, { maximumFractionDigits: 0 })} SF</text>
    </g>
  `;

  svgContent += `</svg>`;

  return svgContent;
};

const RoofMeasurement = () => {
  const dispatch = useDispatch();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const markerRef = useRef(null);
  const reportRef = useRef(null);
  const moreDropdownRef = useRef(null);
  
  // Redux state
  const { leads, loading: leadsLoading } = useSelector(state => state.leads);
  const { projects, loading: projectsLoading } = useSelector(state => state.projects);
  
  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [autoDetectLoading, setAutoDetectLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { title, message, tone: 'info' | 'warning' | 'error' }
  const [measurements, setMeasurements] = useState([]);
  const [roofLines, setRoofLines] = useState([]); // NEW: Track roof lines
  const [totalArea, setTotalArea] = useState(0);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/satellite-streets-v12');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingMode, setDrawingMode] = useState('polygon'); // 'polygon' or 'line'
  const [selectedLineType, setSelectedLineType] = useState('ridge'); // Default line type
  const [showInstructions, setShowInstructions] = useState(true);
  const [currentAddress, setCurrentAddress] = useState('');
  const [activeTab, setActiveTab] = useState('search');
  const [selectedItem, setSelectedItem] = useState(null);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showReport, setShowReport] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingLine, setEditingLine] = useState(null); // NEW: Edit line
  const [defaultPitch, setDefaultPitch] = useState('4');
  const [wasteFactor, setWasteFactor] = useState(0.20);
  const [customerName, setCustomerName] = useState('');
  const [projectDate, setProjectDate] = useState(new Date().toISOString().split('T')[0]);
  const itemsPerPage = 10;

  // Refs to hold latest state values for event handlers
  const measurementsRef = useRef(measurements);
  const roofLinesRef = useRef([]);
  const defaultPitchRef = useRef(defaultPitch);
  const selectedLineTypeRef = useRef(selectedLineType);

  // Live drawing measurements — populated while a draw is in progress so the
  // user can see length/area as they place each vertex, instead of only after
  // the shape is committed.
  const [liveDraw, setLiveDraw] = useState(null);

  // Keep refs updated
  useEffect(() => { measurementsRef.current = measurements; }, [measurements]);
  useEffect(() => { defaultPitchRef.current = defaultPitch; }, [defaultPitch]);
  useEffect(() => { selectedLineTypeRef.current = selectedLineType; }, [selectedLineType]);

  // Close More dropdown on outside click
  useEffect(() => {
    if (!showMoreDropdown) return;
    const handler = (e) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(e.target)) {
        setShowMoreDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMoreDropdown]);

  // Map styles
  const mapStyles = [
    { id: 'mapbox://styles/mapbox/satellite-streets-v12', name: 'Satellite', icon: <FiNavigation /> },
    { id: 'mapbox://styles/mapbox/satellite-v9', name: 'Satellite Only', icon: <FiNavigation /> },
    { id: 'mapbox://styles/mapbox/streets-v12', name: 'Streets', icon: <FiMap /> },
  ];

  // Fetch leads and projects on mount
  useEffect(() => {
    dispatch(fetchLeads({ page: 1, limit: 500, isArchived: false }));
    dispatch(fetchProjects({ page: 1, limit: 500 }));
  }, [dispatch]);

  // Initialize map
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [-98.5795, 39.8283],
      zoom: 4,
      pitch: 0,
      bearing: 0
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');
    map.current.addControl(new mapboxgl.ScaleControl({ maxWidth: 100, unit: 'imperial' }), 'bottom-left');

    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: false, line_string: false, trash: false },
      defaultMode: 'simple_select',
      styles: [
        // Polygon fill - active
        {
          id: 'gl-draw-polygon-fill',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: { 'fill-color': '#3b82f6', 'fill-outline-color': '#3b82f6', 'fill-opacity': 0.3 }
        },
        // Polygon stroke - active
        {
          id: 'gl-draw-polygon-stroke-active',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#3b82f6', 'line-dasharray': [0.2, 2], 'line-width': 3 }
        },
        // Line stroke - active (for drawing)
        {
          id: 'gl-draw-line-active',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#ef4444', 'line-width': 3 }
        },
        // Line stroke - static (completed)
        {
          id: 'gl-draw-line-static',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['==', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#ef4444', 'line-width': 3 }
        },
        // Vertex points - for both polygons and lines
        {
          id: 'gl-draw-polygon-and-line-vertex-active',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          paint: { 'circle-radius': 6, 'circle-color': '#fff', 'circle-stroke-color': '#3b82f6', 'circle-stroke-width': 2 }
        },
        // Midpoint circles
        {
          id: 'gl-draw-polygon-midpoint',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
          paint: { 'circle-radius': 4, 'circle-color': '#3b82f6' }
        },
        // Polygon fill - static
        {
          id: 'gl-draw-polygon-fill-static',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
          paint: { 'fill-color': '#22c55e', 'fill-outline-color': '#22c55e', 'fill-opacity': 0.3 }
        },
        // Polygon stroke - static
        {
          id: 'gl-draw-polygon-stroke-static',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#22c55e', 'line-width': 3 }
        }
      ]
    });

    map.current.addControl(draw.current);
    map.current.on('draw.create', handleDrawCreate);
    map.current.on('draw.update', handleDrawUpdate);
    map.current.on('draw.delete', handleDrawDelete);

    return () => {
      if (markerRef.current) markerRef.current.remove();
      if (map.current) { map.current.remove(); map.current = null; }
    };
  }, []);

  // Update map style
  useEffect(() => {
    if (map.current) {
      map.current.setStyle(mapStyle);
      map.current.once('style.load', () => {
        if (draw.current) {
          const allFeatures = draw.current.getAll();
          if (allFeatures && allFeatures.features.length > 0) {
            setTimeout(() => draw.current.set(allFeatures), 100);
          }
        }
      });
    }
  }, [mapStyle]);

  // Render colored roof lines overlay. Flashing line types (`flash`, `step`)
  // are rendered with a thin dashed guide line plus repeating "X" symbols
  // along their length so they read as flashings on the map.
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    const sourceId = 'roof-lines-overlay';
    const solidLayerId = 'roof-lines-layer';
    const flashLineLayerId = 'roof-lines-flash-layer';
    const flashSymbolLayerId = 'roof-lines-flash-x';

    // Create GeoJSON for lines
    const lineFeatures = roofLines.map(line => ({
      type: 'Feature',
      properties: {
        lineType: line.type,
        color: LINE_TYPES.find(lt => lt.id === line.type)?.color || '#ef4444'
      },
      geometry: {
        type: 'LineString',
        coordinates: line.coordinates
      }
    }));

    const geojson = {
      type: 'FeatureCollection',
      features: lineFeatures
    };

    const flashingFilter = ['in', ['get', 'lineType'], ['literal', FLASHING_LINE_TYPES]];
    const notFlashingFilter = ['!', flashingFilter];

    // Add or update source
    if (map.current.getSource(sourceId)) {
      map.current.getSource(sourceId).setData(geojson);
    } else {
      map.current.addSource(sourceId, { type: 'geojson', data: geojson });

      // Solid colored lines for non-flashing types
      map.current.addLayer({
        id: solidLayerId,
        type: 'line',
        source: sourceId,
        filter: notFlashingFilter,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 4
        }
      });

      // Thin dashed guide line for flashing types
      map.current.addLayer({
        id: flashLineLayerId,
        type: 'line',
        source: sourceId,
        filter: flashingFilter,
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 1.5,
          'line-dasharray': [2, 3],
          'line-opacity': 0.7
        }
      });

      // Repeating "X" symbols along flashing lines
      map.current.addLayer({
        id: flashSymbolLayerId,
        type: 'symbol',
        source: sourceId,
        filter: flashingFilter,
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 26,
          'text-field': 'X',
          'text-size': 16,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-keep-upright': false,
          'text-rotation-alignment': 'viewport'
        },
        paint: {
          'text-color': ['get', 'color'],
          'text-halo-color': '#ffffff',
          'text-halo-width': 2
        }
      });
    }
  }, [roofLines]);

  // Keep a ref of the latest roofLines so the live-draw effect can correctly
  // identify in-progress (not-yet-committed) features.
  useEffect(() => { roofLinesRef.current = roofLines; }, [roofLines]);

  // Live measurement overlay while the user is actively drawing. Renders
  // per-segment length labels and updates the floating banner so the user
  // can see length/area before completing the shape.
  useEffect(() => {
    if (!map.current) return;

    const SRC_ID = 'live-draw-segments';
    const LAYER_ID = 'live-draw-seg-labels';

    let rafId = 0;

    const ensureSource = (haloColor) => {
      if (!map.current.isStyleLoaded()) return false;
      if (!map.current.getSource(SRC_ID)) {
        map.current.addSource(SRC_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        });
        map.current.addLayer({
          id: LAYER_ID,
          type: 'symbol',
          source: SRC_ID,
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 12,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-padding': 2
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': haloColor,
            'text-halo-width': 3
          }
        });
      } else {
        map.current.setPaintProperty(LAYER_ID, 'text-halo-color', haloColor);
      }
      return true;
    };

    const setFeatures = (features) => {
      const src = map.current.getSource(SRC_ID);
      if (src) {
        src.setData({ type: 'FeatureCollection', features });
      }
    };

    const clear = () => {
      const src = map.current && map.current.getSource(SRC_ID);
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      setLiveDraw(null);
    };

    const compute = () => {
      if (!draw.current || !map.current) return;

      const committedIds = new Set([
        ...measurementsRef.current.map((m) => m.id),
        ...roofLinesRef.current.map((l) => l.id)
      ]);

      const all = draw.current.getAll();
      // The in-progress feature is whatever MapboxDraw has that we haven't
      // committed yet (handleDrawCreate adds to committed state on finish).
      const inProgress = all.features.find((f) => !committedIds.has(f.id));
      if (!inProgress) {
        clear();
        return;
      }

      try {
        if (inProgress.geometry.type === 'LineString') {
          const coords = inProgress.geometry.coordinates || [];
          if (coords.length < 2) { clear(); return; }

          const lineType = selectedLineTypeRef.current;
          const color = LINE_TYPES.find((lt) => lt.id === lineType)?.color || '#ef4444';

          const features = [];
          let totalFt = 0;
          for (let i = 0; i < coords.length - 1; i++) {
            const a = coords[i];
            const b = coords[i + 1];
            const d = turf.distance(turf.point(a), turf.point(b), { units: 'feet' });
            totalFt += d;
            features.push({
              type: 'Feature',
              properties: { label: `${Math.round(d)}'` },
              geometry: {
                type: 'Point',
                coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
              }
            });
          }

          ensureSource(color);
          setFeatures(features);
          setLiveDraw({ kind: 'line', totalFt, segmentCount: features.length, color });
        } else if (inProgress.geometry.type === 'Polygon') {
          const ring = (inProgress.geometry.coordinates && inProgress.geometry.coordinates[0]) || [];
          if (ring.length < 2) { clear(); return; }

          const features = [];
          let perimeterFt = 0;
          for (let i = 0; i < ring.length - 1; i++) {
            const a = ring[i];
            const b = ring[i + 1];
            const d = turf.distance(turf.point(a), turf.point(b), { units: 'feet' });
            perimeterFt += d;
            features.push({
              type: 'Feature',
              properties: { label: `${Math.round(d)}'` },
              geometry: {
                type: 'Point',
                coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
              }
            });
          }

          // turf.area needs a closed polygon; during drawing MapboxDraw already
          // closes the ring with a duplicate first vertex. Wrap in try in case
          // an intermediate state is invalid.
          let flatSqFt = 0;
          try {
            const areaSqM = turf.area(inProgress);
            flatSqFt = areaSqM * 10.7639;
          } catch (_) { /* still drawing — area undefined */ }

          const currentPitch = defaultPitchRef.current;
          const actualSqFt = flatSqFt * (PITCH_MULTIPLIERS[currentPitch] || 1);

          ensureSource('#22c55e');
          setFeatures(features);
          setLiveDraw({
            kind: 'polygon',
            flatSqFt,
            actualSqFt,
            perimeterFt,
            vertexCount: ring.length - 1, // last vertex repeats the first
            pitch: currentPitch
          });
        }
      } catch (err) {
        // Don't let a bad intermediate state kill the map.
        // eslint-disable-next-line no-console
        console.warn('Live measurement compute error:', err);
      }
    };

    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(compute);
    };

    if (isDrawing) {
      map.current.on('mousemove', schedule);
      map.current.on('draw.render', schedule);
      map.current.on('draw.update', schedule);
      schedule();
    } else {
      clear();
    }

    return () => {
      cancelAnimationFrame(rafId);
      if (map.current) {
        map.current.off('mousemove', schedule);
        map.current.off('draw.render', schedule);
        map.current.off('draw.update', schedule);
      }
      clear();
    };
  }, [isDrawing]);

  // Handle draw create - add new section or line with default values
  const handleDrawCreate = useCallback((e) => {
    const feature = e.features[0];
    
    if (feature.geometry.type === 'Polygon') {
      const area = turf.area(feature);
      const areaInSqFt = area * 10.7639;
      const perimeter = turf.length(feature, { units: 'feet' });
      const currentPitch = defaultPitchRef.current;
      
      const newSection = {
        id: feature.id,
        name: `Section ${measurementsRef.current.length + 1}`,
        type: 'main',
        pitch: currentPitch,
        flatAreaSqFt: areaInSqFt,
        areaSqFt: areaInSqFt * (PITCH_MULTIPLIERS[currentPitch] || 1),
        perimeterFt: perimeter,
        coordinates: feature.geometry.coordinates
      };
      
      setMeasurements(prev => [...prev, newSection]);
      setIsDrawing(false);
    } else if (feature.geometry.type === 'LineString') {
      // Calculate line length
      const lengthFt = turf.length(feature, { units: 'feet' });
      const lineType = selectedLineTypeRef.current;
      const lineColor = LINE_TYPES.find(lt => lt.id === lineType)?.color || '#ef4444';
      
      // Update the feature with line type property for styling
      const updatedFeature = {
        ...feature,
        properties: {
          ...feature.properties,
          lineType: lineType,
          lineColor: lineColor
        }
      };
      
      // Update the feature in draw with properties
      if (draw.current) {
        draw.current.delete(feature.id);
        draw.current.add(updatedFeature);
      }
      
      const newLine = {
        id: feature.id,
        type: lineType,
        lengthFt: lengthFt,
        coordinates: feature.geometry.coordinates
      };
      
      setRoofLines(prev => [...prev, newLine]);
      setIsDrawing(false);
    }
  }, []);

  // Handle draw update
  const handleDrawUpdate = useCallback((e) => {
    const feature = e.features[0];
    if (feature.geometry.type === 'Polygon') {
      const area = turf.area(feature);
      const areaInSqFt = area * 10.7639;
      const perimeter = turf.length(feature, { units: 'feet' });
      
      setMeasurements(prev => prev.map(m => {
        if (m.id === feature.id) {
          return {
            ...m,
            flatAreaSqFt: areaInSqFt,
            areaSqFt: areaInSqFt * (PITCH_MULTIPLIERS[m.pitch] || 1),
            perimeterFt: perimeter,
            coordinates: feature.geometry.coordinates
          };
        }
        return m;
      }));
    } else if (feature.geometry.type === 'LineString') {
      const lengthFt = turf.length(feature, { units: 'feet' });
      
      setRoofLines(prev => prev.map(l => {
        if (l.id === feature.id) {
          return { ...l, lengthFt, coordinates: feature.geometry.coordinates };
        }
        return l;
      }));
    }
  }, []);

  // Handle draw delete
  const handleDrawDelete = useCallback((e) => {
    const deletedIds = e.features.map(f => f.id);
    setMeasurements(prev => prev.filter(m => !deletedIds.includes(m.id)));
    setRoofLines(prev => prev.filter(l => !deletedIds.includes(l.id)));
  }, []);

  // Update section properties
  const updateSection = useCallback((id, updates) => {
    setMeasurements(prev => prev.map(m => {
      if (m.id === id) {
        const updated = { ...m, ...updates };
        // Recalculate area if pitch changed
        if (updates.pitch !== undefined) {
          updated.areaSqFt = m.flatAreaSqFt * (PITCH_MULTIPLIERS[updates.pitch] || 1);
        }
        return updated;
      }
      return m;
    }));
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    const totalFlat = measurements.reduce((sum, m) => sum + m.flatAreaSqFt, 0);
    const totalActual = measurements.reduce((sum, m) => sum + m.areaSqFt, 0);
    const totalPerimeter = measurements.reduce((sum, m) => sum + m.perimeterFt, 0);
    const withWaste = totalActual * (1 + wasteFactor);
    const squares = totalActual / 100;
    const squaresWithWaste = withWaste / 100;
    
    return {
      totalFlat,
      totalActual,
      totalPerimeter,
      withWaste,
      squares,
      squaresWithWaste,
      sectionCount: measurements.length
    };
  }, [measurements, wasteFactor]);

  // Calculate line totals by type
  const lineTotals = useMemo(() => {
    const byType = {};
    LINE_TYPES.forEach(lt => {
      byType[lt.id] = {
        ...lt,
        totalFt: roofLines.filter(l => l.type === lt.id).reduce((sum, l) => sum + l.lengthFt, 0),
        count: roofLines.filter(l => l.type === lt.id).length
      };
    });
    
    // Calculate drip edge (eave + rake)
    byType.drip.totalFt = byType.eave.totalFt + byType.rake.totalFt;
    
    return byType;
  }, [roofLines]);

  // Update line type
  const updateLine = useCallback((id, updates) => {
    setRoofLines(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  }, []);

  // Delete line
  const deleteLine = useCallback((id) => {
    if (draw.current) draw.current.delete(id);
    setRoofLines(prev => prev.filter(l => l.id !== id));
  }, []);

  // Format address from lead/project
  const formatAddress = useCallback((item) => {
    const address = item.siteContact?.address || item.address || item.client?.address;
    if (!address) return null;
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode) parts.push(address.zipCode);
    return parts.length > 0 ? parts.join(', ') : null;
  }, []);

  // Geocode and zoom to address
  const geocodeAndZoom = useCallback(async (addressString) => {
    if (!addressString) return null;
    setGeocodingLoading(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addressString)}.json?` +
        new URLSearchParams({
          access_token: config.mapboxAccessToken,
          country: 'US',
          types: 'address,place',
          limit: 1
        })
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        if (markerRef.current) markerRef.current.remove();
        markerRef.current = new mapboxgl.Marker({ color: '#3b82f6' })
          .setLngLat([lng, lat])
          .addTo(map.current);
        map.current.flyTo({ center: [lng, lat], zoom: 19, pitch: 0, bearing: 0, duration: 2000 });
        setCurrentAddress(data.features[0].place_name);
        setShowInstructions(true);
        return { lng, lat, address: data.features[0].place_name };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    } finally {
      setGeocodingLoading(false);
    }
  }, []);

  // Ref to hold autoDetectRoof so handleItemSelect can call it without circular deps
  const autoDetectRef = useRef(null);

  // Handle lead/project selection — geocode then auto-detect roof
  const handleItemSelect = useCallback(async (item, type) => {
    setSelectedItem({ ...item, type });
    setCustomerName(item.client?.name || item.siteContact?.name || item.leadContact?.name || '');
    const address = formatAddress(item);
    if (address) {
      const result = await geocodeAndZoom(address + ', USA');
      if (result) {
        setTimeout(() => { if (autoDetectRef.current) autoDetectRef.current(); }, 2500);
      }
    }
  }, [formatAddress, geocodeAndZoom]);

  // Search for address
  const handleSearch = async (query) => {
    if (!query || query.length < 3) { setSearchResults([]); return; }
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
        new URLSearchParams({ access_token: config.mapboxAccessToken, country: 'US', types: 'address,place,postcode', limit: 5 })
      );
      const data = await response.json();
      if (data.features) { setSearchResults(data.features); setShowResults(true); }
    } catch (error) { console.error('Search error:', error); }
  };

  const handleSearchChange = (e) => { setSearchQuery(e.target.value); handleSearch(e.target.value); };

  const selectResult = (result) => {
    const [lng, lat] = result.center;
    if (markerRef.current) markerRef.current.remove();
    markerRef.current = new mapboxgl.Marker({ color: '#3b82f6' }).setLngLat([lng, lat]).addTo(map.current);
    map.current.flyTo({ center: [lng, lat], zoom: 19, pitch: 0, bearing: 0, duration: 2000 });
    setCurrentAddress(result.place_name);
    setSearchQuery(result.place_name);
    setShowResults(false);
    setShowInstructions(true);
    setSelectedItem(null);
  };

  // Filter leads/projects
  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    if (!listSearchTerm) return leads;
    const searchLower = listSearchTerm.toLowerCase();
    return leads.filter(lead => {
      const name = (lead.siteContact?.name || lead.client?.name || lead.leadContact?.name || '').toLowerCase();
      const jobNumber = (lead.jobNumber || '').toLowerCase();
      const address = formatAddress(lead)?.toLowerCase() || '';
      return name.includes(searchLower) || jobNumber.includes(searchLower) || address.includes(searchLower);
    });
  }, [leads, listSearchTerm, formatAddress]);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    if (!listSearchTerm) return projects;
    const searchLower = listSearchTerm.toLowerCase();
    return projects.filter(project => {
      const name = (project.client?.name || project.siteContact?.name || '').toLowerCase();
      const jobNumber = (project.jobNumber || '').toLowerCase();
      const address = formatAddress(project)?.toLowerCase() || '';
      return name.includes(searchLower) || jobNumber.includes(searchLower) || address.includes(searchLower);
    });
  }, [projects, listSearchTerm, formatAddress]);

  const paginatedItems = useMemo(() => {
    const items = activeTab === 'leads' ? filteredLeads : filteredProjects;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  }, [activeTab, filteredLeads, filteredProjects, currentPage]);

  const totalPages = useMemo(() => {
    const items = activeTab === 'leads' ? filteredLeads : filteredProjects;
    return Math.ceil(items.length / itemsPerPage);
  }, [activeTab, filteredLeads, filteredProjects]);

  useEffect(() => { setCurrentPage(1); setListSearchTerm(''); }, [activeTab]);

  const startDrawingPolygon = () => { 
    if (draw.current) { 
      draw.current.changeMode('draw_polygon'); 
      setDrawingMode('polygon');
      setIsDrawing(true); 
      setShowInstructions(false); 
    } 
  };
  
  const startDrawingLine = (lineType) => { 
    if (draw.current) { 
      setSelectedLineType(lineType);
      draw.current.changeMode('draw_line_string'); 
      setDrawingMode('line');
      setIsDrawing(true); 
      setShowInstructions(false); 
    } 
  };
  
  const clearAllDrawings = () => { 
    if (draw.current) { 
      draw.current.deleteAll(); 
      setMeasurements([]); 
      setRoofLines([]);
    } 
  };

  // Wait for the map to finish loading all tiles and become idle
  const waitForMapIdle = useCallback(() => {
    return new Promise((resolve) => {
      if (!map.current) { resolve(); return; }
      if (!map.current.isMoving() && map.current.areTilesLoaded()) {
        resolve();
      } else {
        const timeout = setTimeout(resolve, 6000);
        map.current.once('idle', () => { clearTimeout(timeout); resolve(); });
      }
    });
  }, []);

  // Extract polygon coordinates from a feature (handles Polygon + MultiPolygon)
  const extractBuildingPolygon = useCallback((feature) => {
    if (!feature) return null;
    if (feature.geometry.type === 'Polygon') {
      return feature.geometry.coordinates;
    }
    if (feature.geometry.type === 'MultiPolygon') {
      let largestArea = 0;
      let bestCoords = null;
      feature.geometry.coordinates.forEach(coords => {
        const poly = turf.polygon(coords);
        const a = turf.area(poly);
        if (a > largestArea) {
          largestArea = a;
          bestCoords = coords;
        }
      });
      return bestCoords;
    }
    return null;
  }, []);

  // Query rendered building features from the map at a given point
  const queryBuildingsOnMap = useCallback((lng, lat, radius = 50) => {
    if (!map.current) return [];
    const point = map.current.project([lng, lat]);
    const bbox = [
      [point.x - radius, point.y - radius],
      [point.x + radius, point.y + radius]
    ];

    // Find all building-related layer IDs in the current style
    const style = map.current.getStyle();
    if (!style || !style.layers) return [];
    const buildingLayerIds = style.layers
      .filter(l => {
        const id = (l.id || '').toLowerCase();
        const sourceLayer = (l['source-layer'] || '').toLowerCase();
        return id.includes('building') || sourceLayer.includes('building');
      })
      .map(l => l.id);

    if (buildingLayerIds.length === 0) {
      // No explicit building layers (e.g. satellite-only style) — query all layers
      return map.current.queryRenderedFeatures(bbox)
        .filter(f => {
          const sl = (f.sourceLayer || '').toLowerCase();
          return sl.includes('building') && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon');
        });
    }

    return map.current.queryRenderedFeatures(bbox, { layers: buildingLayerIds })
      .filter(f => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon');
  }, []);

  // Auto-detect building footprint from Mapbox vector tiles
  const autoDetectRoof = useCallback(async () => {
    if (!map.current) return;
    setAutoDetectLoading(true);

    try {
      // Require a marker or zoomed-in view — prevents searching empty areas
      if (!markerRef.current && map.current.getZoom() < 14) {
        setDialog({
          title: 'Pick a location first',
          message: 'Search for an address or select a lead/project, then try Auto Detect again.',
          tone: 'info'
        });
        setAutoDetectLoading(false);
        return;
      }

      const targetLngLat = markerRef.current
        ? markerRef.current.getLngLat()
        : map.current.getCenter();
      const { lng, lat } = targetLngLat;

      // Switch to satellite-streets if on satellite-only (it has no building source)
      const currentStyle = map.current.getStyle();
      const hasBuildings = currentStyle && currentStyle.sources && (
        Object.keys(currentStyle.sources).some(s => s === 'composite') ||
        currentStyle.layers?.some(l => (l['source-layer'] || '').includes('building'))
      );
      if (!hasBuildings) {
        setMapStyle('mapbox://styles/mapbox/satellite-streets-v12');
        await new Promise(resolve => {
          map.current.once('style.load', resolve);
          setTimeout(resolve, 3000);
        });
      }

      // Zoom in if too far out — building data requires zoom >= 15
      const needsZoom = map.current.getZoom() < 16;
      if (needsZoom) {
        map.current.flyTo({ center: [lng, lat], zoom: 19, pitch: 0, bearing: 0, duration: 1800 });
      } else {
        map.current.panTo([lng, lat], { duration: 500 });
      }
      // Wait for map to finish moving and load tiles
      await waitForMapIdle();

      let buildingPolygon = null;

      // Strategy 1: queryRenderedFeatures (fastest, works with loaded tiles)
      const renderedFeatures = queryBuildingsOnMap(lng, lat, 50);
      if (renderedFeatures.length > 0) {
        // Pick the feature closest to the target point
        const targetPoint = turf.point([lng, lat]);
        let closestDist = Infinity;
        let closestFeature = null;
        renderedFeatures.forEach(f => {
          try {
            const centroid = turf.centroid(f);
            const dist = turf.distance(targetPoint, centroid);
            if (dist < closestDist) {
              closestDist = dist;
              closestFeature = f;
            }
          } catch (_) { /* skip invalid geometries */ }
        });
        buildingPolygon = extractBuildingPolygon(closestFeature || renderedFeatures[0]);
      }

      // Strategy 2: Mapbox Tilequery API (works even if layer isn't rendered)
      if (!buildingPolygon) {
        try {
          const tileQueryUrl = `https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery/${lng},${lat}.json?` +
            new URLSearchParams({
              layers: 'building',
              radius: 50,
              limit: 10,
              access_token: config.mapboxAccessToken
            });
          const response = await fetch(tileQueryUrl);
          if (response.ok) {
            const data = await response.json();
            if (data.features && data.features.length > 0) {
              // Pick closest polygon
              const targetPoint = turf.point([lng, lat]);
              let closestDist = Infinity;
              let bestFeature = null;
              data.features.forEach(f => {
                if (f.geometry.type !== 'Polygon' && f.geometry.type !== 'MultiPolygon') return;
                try {
                  const centroid = turf.centroid(f);
                  const dist = turf.distance(targetPoint, centroid);
                  if (dist < closestDist) {
                    closestDist = dist;
                    bestFeature = f;
                  }
                } catch (_) { /* skip */ }
              });
              if (!bestFeature) {
                bestFeature = data.features.find(f => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon');
              }
              buildingPolygon = extractBuildingPolygon(bestFeature);
            }
          }
        } catch (err) {
          console.warn('Tilequery failed:', err);
        }
      }

      // Strategy 3: querySourceFeatures on the composite source (works even if building layer isn't visible)
      if (!buildingPolygon) {
        try {
          const style = map.current.getStyle();
          const sourceIds = Object.keys(style.sources || {});
          const targetPoint = turf.point([lng, lat]);
          for (const sourceId of sourceIds) {
            const sourceFeatures = map.current.querySourceFeatures(sourceId, {
              sourceLayer: 'building'
            });
            // Filter to polygons that contain our target point
            const candidates = sourceFeatures.filter(f =>
              (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
            );
            if (candidates.length > 0) {
              // Find the one that contains or is closest to our target
              let best = null;
              let bestDist = Infinity;
              for (const c of candidates) {
                try {
                  if (turf.booleanPointInPolygon(targetPoint, c)) {
                    best = c;
                    break;
                  }
                  const dist = turf.distance(targetPoint, turf.centroid(c));
                  if (dist < bestDist) {
                    bestDist = dist;
                    best = c;
                  }
                } catch (_) { /* skip invalid */ }
              }
              if (best) {
                buildingPolygon = extractBuildingPolygon(best);
                if (buildingPolygon) break;
              }
            }
          }
        } catch (err) {
          console.warn('querySourceFeatures fallback:', err);
        }
      }

      // Strategy 4: Retry rendered features with larger radius after tiles settle
      if (!buildingPolygon) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retryFeatures = queryBuildingsOnMap(lng, lat, 100);
        if (retryFeatures.length > 0) {
          buildingPolygon = extractBuildingPolygon(retryFeatures[0]);
        }
      }

      if (!buildingPolygon) {
        setDialog({
          title: 'No building detected',
          message: 'We could not find a building footprint at this location. Make sure the pin is on a roof, try the Satellite map style, or use the manual Outline tool. Some rural areas may not have building data available.',
          tone: 'warning'
        });
        return;
      }

      // Add the detected polygon to MapboxDraw
      const featureId = `auto_${Date.now()}`;
      const feature = {
        id: featureId,
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: buildingPolygon
        }
      };

      if (draw.current) {
        draw.current.add(feature);
      }

      // Calculate measurements and add to state
      const polygon = turf.polygon(buildingPolygon);
      const area = turf.area(polygon);
      const areaInSqFt = area * 10.7639;
      const perimeter = turf.length(polygon, { units: 'feet' });
      const currentPitch = defaultPitchRef.current;

      const newSection = {
        id: featureId,
        name: `Section ${measurementsRef.current.length + 1}`,
        type: 'main',
        pitch: currentPitch,
        flatAreaSqFt: areaInSqFt,
        areaSqFt: areaInSqFt * (PITCH_MULTIPLIERS[currentPitch] || 1),
        perimeterFt: perimeter,
        coordinates: buildingPolygon
      };

      setMeasurements(prev => [...prev, newSection]);
      setShowInstructions(false);

    } catch (error) {
      console.error('Auto-detect error:', error);
      setDialog({
        title: 'Auto-detect failed',
        message: 'Something went wrong while detecting the building. Please try again, or use the manual Outline tool to draw the roof.',
        tone: 'error'
      });
    } finally {
      setAutoDetectLoading(false);
    }
  }, [waitForMapIdle, queryBuildingsOnMap, extractBuildingPolygon]);

  // Keep ref in sync so handleItemSelect can call it
  useEffect(() => { autoDetectRef.current = autoDetectRoof; }, [autoDetectRoof]);
  
  const deleteDrawing = (id) => { 
    if (draw.current) { 
      draw.current.delete(id); 
      setMeasurements(prev => prev.filter(m => m.id !== id)); 
    } 
  };
  
  const undoLastDrawing = () => { 
    // Undo last polygon or line
    const lastPolygon = measurements[measurements.length - 1];
    const lastLine = roofLines[roofLines.length - 1];
    
    if (lastLine && (!lastPolygon || lastLine.id > lastPolygon?.id)) {
      if (draw.current) draw.current.delete(lastLine.id);
      setRoofLines(prev => prev.slice(0, -1));
    } else if (lastPolygon) {
      if (draw.current) draw.current.delete(lastPolygon.id);
      setMeasurements(prev => prev.slice(0, -1));
    }
  };

  // Print report
  const printReport = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateReportHTML());
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  // Generate report HTML
  const generateReportHTML = () => {
    const date = new Date(projectDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const diagramSVG = generateDiagramSVG(measurements, roofLines, 700, 500);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Roof Measurement Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0f172a; padding: 40px; max-width: 900px; margin: 0 auto; background: #ffffff; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #4f46e5; }
          .logo { background: #4f46e5; color: white; padding: 10px 20px; font-weight: 700; font-size: 22px; border-radius: 8px; letter-spacing: -0.02em; }
          .title { font-size: 26px; font-weight: 400; color: #334155; letter-spacing: -0.02em; }
          .title span { font-weight: 600; color: #0f172a; }
          .info { text-align: right; font-size: 13px; color: #64748b; }
          .info p { margin-bottom: 4px; }
          .info strong { color: #0f172a; font-weight: 600; }
          .section { margin-bottom: 30px; page-break-inside: avoid; }
          .section-title { font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 12px; }
          .diagram-container { display: flex; justify-content: center; margin: 16px 0; padding: 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
          .summary-box { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
          .summary-box h4 { font-size: 10px; text-transform: uppercase; color: #64748b; margin-bottom: 8px; letter-spacing: 0.08em; font-weight: 600; }
          .summary-box .value { font-size: 26px; font-weight: 600; color: #0f172a; letter-spacing: -0.02em; }
          .summary-box .unit { font-size: 12px; color: #64748b; font-weight: 400; margin-left: 2px; }
          .summary-box.highlight { background: #4f46e5; color: white; border-color: #4f46e5; }
          .summary-box.highlight h4 { color: #c7d2fe; }
          .summary-box.highlight .value { color: white; }
          .summary-box.highlight .unit { color: #c7d2fe; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #f1f5f9; }
          th { background: #f8fafc; font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 600; letter-spacing: 0.06em; }
          td { font-size: 13px; color: #0f172a; }
          tr:last-child td { border-bottom: none; }
          .type-badge { display: inline-block; padding: 3px 8px; border-radius: 6px; font-size: 10px; font-weight: 600; text-transform: capitalize; letter-spacing: 0.02em; }
          .pitch-table { margin-top: 16px; font-size: 11px; }
          .pitch-table th, .pitch-table td { text-align: center; padding: 8px 4px; }
          .pitch-table .selected { background: #4f46e5; color: white; font-weight: 700; }
          .waste-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; margin-top: 12px; }
          .waste-box { text-align: center; padding: 12px 6px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 11px; }
          .waste-box.selected { background: #4f46e5; color: white; border-color: #4f46e5; }
          .waste-box .label { font-weight: 700; font-size: 13px; }
          .waste-box .squares { color: #64748b; margin-top: 4px; font-size: 10px; }
          .waste-box.selected .squares { color: #c7d2fe; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
          .notes { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-top: 20px; font-size: 11px; color: #475569; line-height: 1.6; }
          .notes strong { color: #0f172a; }
          .page-break { page-break-before: always; }
          .lengths-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-top: 12px; }
          .length-box { text-align: center; padding: 12px 8px; border-radius: 10px; background: #ffffff; }
          .length-box .label { font-size: 10px; text-transform: uppercase; color: #64748b; margin-bottom: 4px; letter-spacing: 0.06em; font-weight: 600; }
          .length-box .value { font-size: 20px; font-weight: 600; letter-spacing: -0.02em; }
          @media print { 
            body { padding: 20px; } 
            .no-print { display: none; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <!-- Page 1: Overview -->
        <div class="header">
          <div>
            <div class="logo">JRT</div>
            <div class="title"><span>Roof</span>Measure</div>
          </div>
          <div class="info">
            <p><strong>${currentAddress || 'Address Not Specified'}</strong></p>
            <p>${date}</p>
            ${customerName ? `<p>Prepared For: ${customerName}</p>` : ''}
          </div>
        </div>

        <div class="section">
          <h3 class="section-title">Roof Diagram</h3>
          <div class="diagram-container">
            ${diagramSVG}
          </div>
        </div>

        ${roofLines.length > 0 ? `
        <div class="section">
          <h3 class="section-title">Lengths</h3>
          <div class="lengths-grid">
            ${LINE_TYPES.map(lt => {
              const total = roofLines.filter(l => l.type === lt.id).reduce((sum, l) => sum + l.lengthFt, 0);
              const bgColor = lt.id === 'ridge' || lt.id === 'hip' ? '#fef2f2' : 
                              lt.id === 'valley' ? '#f5f3ff' : 
                              lt.id === 'rake' ? '#f0fdf4' : 
                              lt.id === 'eave' ? '#f9fafb' : '#fefce8';
              return total > 0 || lt.id === 'drip' ? `
                <div class="length-box" style="background: ${bgColor}; border: 2px solid ${lt.color}">
                  <div style="width: 100%; height: 4px; background: ${lt.color}; border-radius: 2px; margin-bottom: 6px;"></div>
                  <div class="label">${lt.label}</div>
                  <div class="value" style="color: ${lt.color}">${lt.id === 'drip' ? Math.round((lineTotals.eave?.totalFt || 0) + (lineTotals.rake?.totalFt || 0)) : Math.round(total)}</div>
                </div>
              ` : '';
            }).filter(Boolean).join('')}
          </div>
        </div>
        ` : ''}

        <div class="section">
          <h3 class="section-title">Measurement Summary</h3>
          <div class="summary-grid">
            <div class="summary-box highlight">
              <h4>Total Roof Area</h4>
              <div class="value">${totals.totalActual.toLocaleString(undefined, { maximumFractionDigits: 0 })}<span class="unit"> sq ft</span></div>
            </div>
            <div class="summary-box highlight">
              <h4>Roofing Squares</h4>
              <div class="value">${totals.squares.toFixed(1)}<span class="unit"> squares</span></div>
            </div>
            <div class="summary-box highlight">
              <h4>With ${(wasteFactor * 100).toFixed(0)}% Waste</h4>
              <div class="value">${totals.squaresWithWaste.toFixed(1)}<span class="unit"> squares</span></div>
            </div>
            <div class="summary-box">
              <h4>Flat Area</h4>
              <div class="value">${totals.totalFlat.toLocaleString(undefined, { maximumFractionDigits: 0 })}<span class="unit"> sq ft</span></div>
            </div>
            <div class="summary-box">
              <h4>Total Perimeter</h4>
              <div class="value">${totals.totalPerimeter.toFixed(0)}<span class="unit"> linear ft</span></div>
            </div>
            <div class="summary-box">
              <h4>Roof Sections</h4>
              <div class="value">${totals.sectionCount}<span class="unit"> sections</span></div>
            </div>
          </div>
        </div>

        <!-- Page 2: Details -->
        <div class="page-break"></div>
        
        <div class="header">
          <div>
            <div class="logo">JRT</div>
            <div class="title"><span>Roof</span>Measure</div>
          </div>
          <div class="info">
            <p><strong>Section Details</strong></p>
            <p>${currentAddress || ''}</p>
          </div>
        </div>

        <div class="section">
          <h3 class="section-title">Section Breakdown</h3>
          <table>
            <thead>
              <tr>
                <th>Section</th>
                <th>Type</th>
                <th>Pitch</th>
                <th>Multiplier</th>
                <th>Flat Area</th>
                <th>Actual Area</th>
                <th>Perimeter</th>
              </tr>
            </thead>
            <tbody>
              ${measurements.map(m => {
                const sectionType = SECTION_TYPES.find(t => t.id === m.type);
                return `
                  <tr>
                    <td><strong>${m.name}</strong></td>
                    <td><span class="type-badge" style="background: ${sectionType?.color}20; color: ${sectionType?.color}">${sectionType?.label || m.type}</span></td>
                    <td>${m.pitch}/12</td>
                    <td>×${PITCH_MULTIPLIERS[m.pitch]?.toFixed(3) || '1.000'}</td>
                    <td>${m.flatAreaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</td>
                    <td><strong>${m.areaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</strong></td>
                    <td>${m.perimeterFt.toFixed(0)} ft</td>
                  </tr>
                `;
              }).join('')}
              <tr style="background: #f8fafc; font-weight: 700;">
                <td colspan="4">TOTALS</td>
                <td>${totals.totalFlat.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</td>
                <td style="color: #4f46e5;">${totals.totalActual.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</td>
                <td>${totals.totalPerimeter.toFixed(0)} ft</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <h3 class="section-title">Pitch Multiplier Reference</h3>
          <table class="pitch-table">
            <thead>
              <tr>
                <th>Pitch</th>
                ${Object.keys(PITCH_MULTIPLIERS).slice(0, 13).map(p => `<th class="${measurements.some(m => m.pitch === p) ? 'selected' : ''}">${p}/12</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Multiplier</strong></td>
                ${Object.entries(PITCH_MULTIPLIERS).slice(0, 13).map(([p, v]) => `<td class="${measurements.some(m => m.pitch === p) ? 'selected' : ''}">${v.toFixed(3)}</td>`).join('')}
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <h3 class="section-title">Waste Factor Options</h3>
          <div class="waste-grid">
            ${WASTE_FACTORS.map(w => {
              const area = totals.totalActual * (1 + w.value);
              const squares = area / 100;
              return `
                <div class="waste-box ${w.value === wasteFactor ? 'selected' : ''}">
                  <div class="label">${w.label}</div>
                  <div class="squares">${squares.toFixed(1)} sq</div>
                </div>
              `;
            }).join('')}
          </div>
          <p style="margin-top: 10px; font-size: 11px; color: #6b7280; text-align: center;">
            Selected: <strong>${(wasteFactor * 100).toFixed(0)}%</strong> waste factor = <strong>${totals.squaresWithWaste.toFixed(1)}</strong> squares (${totals.withWaste.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft)
          </p>
        </div>

        <div class="notes">
          <strong>Notes:</strong>
          <ol style="margin-top: 8px; padding-left: 20px; line-height: 1.6;">
            <li>Measurements are calculated from satellite imagery and may vary from actual roof dimensions.</li>
            <li>Actual roof area accounts for roof pitch using standard multipliers (flat area × pitch multiplier).</li>
            <li>Waste factor accounts for cuts, overlaps, starter, ridge cap, and material waste.</li>
            <li>1 roofing square = 100 square feet of roof area.</li>
            <li>Always verify measurements on-site before ordering materials.</li>
          </ol>
        </div>

        <div class="footer">
          <p>Generated by JRT CRM Roof Measurement Tool • ${new Date().toLocaleString()}</p>
          <p style="margin-top: 5px;">Measurements in feet (') • Areas in square feet (sq ft)</p>
        </div>
      </body>
      </html>
    `;
  };

  // Item Card Component
  const ItemCard = ({ item, type, isSelected }) => {
    const address = formatAddress(item);
    const name = type === 'leads' 
      ? (item.siteContact?.name || item.client?.name || item.leadContact?.name || 'Unknown')
      : (item.client?.name || item.siteContact?.name || 'Unknown');
    const status = type === 'leads' ? item.priority : item.projectStatus;
    const statusStyle = type === 'leads'
      ? item.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' : item.priority === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : item.projectStatus === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200';

    return (
      <div
        onClick={() => handleItemSelect(item, type)}
        className={`p-3 rounded-lg border cursor-pointer transition-all ${
          isSelected
            ? 'border-indigo-500 bg-indigo-50/40 ring-2 ring-indigo-100'
            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50'
        }`}
      >
        <div className="flex items-start justify-between mb-1.5 gap-2">
          <span className="text-[11px] font-semibold text-slate-500 tabular-nums">{item.jobNumber}</span>
          {status && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium border capitalize ${statusStyle}`}>
              {status}
            </span>
          )}
        </div>
        <p className="font-medium text-slate-900 text-sm mb-1 truncate">{name}</p>
        {address && (
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            <FiMapPin className="w-2.5 h-2.5 flex-shrink-0 text-slate-400" />
            <span className="truncate">{address}</span>
          </p>
        )}
      </div>
    );
  };

  // Section Editor Component
  const SectionEditor = ({ section, onClose }) => {
    const [localSection, setLocalSection] = useState(section);
    
    const handleSave = () => {
      updateSection(section.id, localSection);
      onClose();
    };
    
    return (
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Edit Section</h3>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors">
              <FiX className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>
          <div className="px-5 py-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Section Name</label>
              <input
                type="text"
                value={localSection.name}
                onChange={(e) => setLocalSection({ ...localSection, name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Section Type</label>
              <div className="grid grid-cols-3 gap-1.5">
                {SECTION_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setLocalSection({ ...localSection, type: type.id })}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors border ${
                      localSection.type === type.id
                        ? 'text-white border-transparent'
                        : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200'
                    }`}
                    style={localSection.type === type.id ? { backgroundColor: type.color } : {}}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Roof Pitch</label>
              <select
                value={localSection.pitch}
                onChange={(e) => setLocalSection({ ...localSection, pitch: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-colors"
              >
                {Object.keys(PITCH_MULTIPLIERS).map(p => (
                  <option key={p} value={p}>{p}/12 (×{PITCH_MULTIPLIERS[p].toFixed(3)})</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
                <span className="text-slate-700 font-medium tabular-nums">{localSection.flatAreaSqFt?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> sq ft flat
                <span className="text-slate-400 mx-1.5">→</span>
                <span className="text-indigo-700 font-semibold tabular-nums">{(localSection.flatAreaSqFt * (PITCH_MULTIPLIERS[localSection.pitch] || 1)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> sq ft actual
              </p>
            </div>
          </div>
          <div className="px-5 py-4 border-t border-slate-200 flex gap-2">
            <button onClick={onClose} className="flex-1 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
              <FiCheck className="w-3.5 h-3.5" /> Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <DashboardHeader hideCreateLead hideActions hideTabBar hideSearch />
      
      <div className="h-[calc(100vh-80px)] flex flex-col md:flex-row bg-slate-50">
        {/* Sidebar */}
        <div className="w-full md:w-[400px] bg-white border-r border-slate-200 flex flex-col z-10 md:h-full h-auto max-h-[50vh] md:max-h-full overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center shadow-sm shadow-indigo-200">
                  <FiCrosshair className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h2 className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight">Roof Measurement</h2>
                  <p className="text-slate-500 text-xs">Satellite-based estimator</p>
                </div>
              </div>
              {measurements.length > 0 && (
                <button
                  onClick={() => setShowReport(true)}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors flex items-center gap-1.5 text-white text-xs font-medium shadow-sm shadow-indigo-200"
                  title="View Report"
                >
                  <FiFileText className="w-3.5 h-3.5" />
                  <span>Report</span>
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex px-2 pt-2 border-b border-slate-200 bg-white gap-1">
            {['search', 'leads', 'projects'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors rounded-t-lg border-b-2 ${
                  activeTab === tab
                    ? 'text-indigo-600 border-indigo-600'
                    : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                {tab === 'search' && <FiSearch className="w-3 h-3" />}
                {tab === 'leads' && <FiUsers className="w-3 h-3" />}
                {tab === 'projects' && <FiClipboard className="w-3 h-3" />}
                <span className="capitalize">{tab}</span>
                {tab === 'leads' && leads?.length > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${activeTab === tab ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-600'}`}>{leads.length}</span>}
                {tab === 'projects' && projects?.length > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${activeTab === tab ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-600'}`}>{projects.length}</span>}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {activeTab === 'search' ? (
              <>
                {/* Search */}
                <div className="px-5 py-4 border-b border-slate-200">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                    <input
                      type="text"
                      placeholder="Search address…"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onFocus={() => searchResults.length > 0 && setShowResults(true)}
                      className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-colors placeholder:text-slate-400"
                    />
                    {showResults && searchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-60 overflow-auto">
                        {searchResults.map((result) => (
                          <button key={result.id} onClick={() => selectResult(result)} className="w-full px-3.5 py-2.5 text-left hover:bg-slate-50 flex items-start gap-2.5 border-b border-slate-100 last:border-b-0 transition-colors">
                            <FiMapPin className="w-3.5 h-3.5 text-slate-400 mt-1 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{result.text}</p>
                              <p className="text-xs text-slate-500 truncate">{result.place_name}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {currentAddress && (
                    <div className="mt-2.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-md flex items-center gap-1.5">
                      <FiMapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <p className="text-xs text-slate-600 truncate">{currentAddress}</p>
                    </div>
                  )}
                </div>

                {/* Default Pitch - Simplified in sidebar */}
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-600 text-xs font-medium">Pitch</span>
                    <select value={defaultPitch} onChange={(e) => setDefaultPitch(e.target.value)} className="flex-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded-md text-xs font-medium text-slate-700 focus:outline-none focus:border-indigo-400">
                      {Object.keys(PITCH_MULTIPLIERS).map(p => <option key={p} value={p}>{p}/12</option>)}
                    </select>
                    <button onClick={undoLastDrawing} disabled={measurements.length === 0 && roofLines.length === 0} className={`p-1.5 rounded-md transition-colors ${(measurements.length === 0 && roofLines.length === 0) ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-200'}`} title="Undo">
                      <FiRotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={clearAllDrawings} disabled={measurements.length === 0 && roofLines.length === 0} className={`p-1.5 rounded-md transition-colors ${(measurements.length === 0 && roofLines.length === 0) ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`} title="Clear All">
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Instructions or Measurements */}
                {showInstructions && measurements.length === 0 ? (
                  <div className="px-5 py-4 border-b border-slate-200">
                    <div className="flex items-start gap-3 p-3.5 bg-slate-50 border border-slate-200 rounded-lg">
                      <FiInfo className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-slate-600">
                        <p className="font-semibold text-slate-800 mb-1.5">Quick start</p>
                        <ol className="list-decimal list-inside space-y-1 marker:text-slate-400">
                          <li>Search an address or pick from Leads/Projects</li>
                          <li>Use Auto Detect, or click Outline to draw manually</li>
                          <li>Click points on the roof; double-click to finish</li>
                          <li>Tap a section to edit pitch &amp; type</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Measurements List */}
                <div className="flex-1 overflow-auto">
                  {(measurements.length > 0 || roofLines.length > 0) ? (
                    <div className="p-5 space-y-5">
                      {/* Area Sections */}
                      {measurements.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2.5">
                            <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                              <FiHash className="w-3 h-3" /> Areas
                            </h3>
                            <span className="text-[11px] font-medium text-slate-400">{measurements.length}</span>
                          </div>
                          <div className="space-y-2">
                            {measurements.map((m) => {
                              const sectionType = SECTION_TYPES.find(t => t.id === m.type);
                              return (
                                <div key={m.id} className="bg-white rounded-lg p-3 border border-slate-200 hover:border-slate-300 transition-colors">
                                  <div className="flex items-center justify-between mb-2.5">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: sectionType?.color }} />
                                      <span className="font-medium text-slate-900 text-sm truncate">{m.name}</span>
                                      <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-medium flex-shrink-0">{m.pitch}/12</span>
                                    </div>
                                    <div className="flex items-center gap-0.5 flex-shrink-0">
                                      <button onClick={() => setEditingSection(m)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-700 transition-colors">
                                        <FiEdit2 className="w-3 h-3" />
                                      </button>
                                      <button onClick={() => deleteDrawing(m.id)} className="p-1.5 hover:bg-red-50 rounded-md text-slate-400 hover:text-red-500 transition-colors">
                                        <FiTrash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-1.5 text-xs">
                                    <div className="bg-slate-50 rounded-md p-2">
                                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Flat</p>
                                      <p className="font-semibold text-slate-900 tabular-nums">{m.flatAreaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    </div>
                                    <div className="bg-indigo-50 rounded-md p-2">
                                      <p className="text-[10px] text-indigo-600 uppercase tracking-wider mb-0.5">Actual</p>
                                      <p className="font-semibold text-indigo-700 tabular-nums">{m.areaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    </div>
                                    <div className="bg-slate-50 rounded-md p-2">
                                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Perim</p>
                                      <p className="font-semibold text-slate-900 tabular-nums">{m.perimeterFt.toFixed(0)}<span className="text-[10px] text-slate-500 ml-0.5">ft</span></p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Roof Lines */}
                      {roofLines.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2.5">
                            <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                              <FiCrosshair className="w-3 h-3" /> Lines
                            </h3>
                            <span className="text-[11px] font-medium text-slate-400">{roofLines.length}</span>
                          </div>
                          
                          {/* Line Totals by Type */}
                          {LINE_TYPES.filter(lt => lineTotals[lt.id]?.totalFt > 0).length > 0 && (
                            <div className="grid grid-cols-4 gap-1.5 mb-3">
                              {LINE_TYPES.filter(lt => lineTotals[lt.id]?.totalFt > 0).map(lt => (
                                <div 
                                  key={lt.id}
                                  className="rounded-md p-2 text-center bg-white border border-slate-200"
                                >
                                  <div className="w-full h-0.5 rounded-full mb-1.5" style={{ backgroundColor: lt.color }} />
                                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: lt.color }}>{lt.label}</p>
                                  <p className="text-xs font-semibold text-slate-900 tabular-nums">{Math.round(lineTotals[lt.id].totalFt)}<span className="text-[9px] text-slate-500 ml-0.5">ft</span></p>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Individual Lines */}
                          <div className="space-y-1 max-h-44 overflow-auto pr-1">
                            {roofLines.map((line) => {
                              const lineType = LINE_TYPES.find(t => t.id === line.type);
                              const flashing = isFlashingLineType(line.type);
                              return (
                                <div 
                                  key={line.id} 
                                  className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200/70 hover:border-slate-300 transition-colors"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    {flashing ? (
                                      <span
                                        className="inline-flex items-center justify-center w-3.5 h-3.5 font-bold text-[11px] leading-none flex-shrink-0"
                                        style={{ color: lineType?.color }}
                                        aria-label="Flashing marker"
                                      >
                                        ✕
                                      </span>
                                    ) : (
                                      <div className="w-3.5 h-[3px] rounded-sm flex-shrink-0" style={{ backgroundColor: lineType?.color }} />
                                    )}
                                    <span className="text-xs font-medium text-slate-700 truncate">{lineType?.label}</span>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className="text-xs font-semibold text-slate-900 tabular-nums">{Math.round(line.lengthFt)}<span className="text-[10px] text-slate-500 ml-0.5">ft</span></span>
                                    <button 
                                      onClick={() => setEditingLine(line)}
                                      className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-white transition-colors"
                                    >
                                      <FiEdit2 className="w-2.5 h-2.5" />
                                    </button>
                                    <button 
                                      onClick={() => deleteLine(line.id)}
                                      className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-white transition-colors"
                                    >
                                      <FiTrash2 className="w-2.5 h-2.5" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="px-8 py-12 text-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FiCrosshair className="w-5 h-5 text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-700">No measurements yet</p>
                      <p className="text-xs text-slate-500 mt-1">Search an address and click Auto Detect to start.</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Leads/Projects List */}
                <div className="px-5 py-4 border-b border-slate-200">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                    <input
                      type="text"
                      placeholder={`Search ${activeTab}…`}
                      value={listSearchTerm}
                      onChange={(e) => { setListSearchTerm(e.target.value); setCurrentPage(1); }}
                      className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-colors placeholder:text-slate-400"
                    />
                  </div>
                </div>

                {selectedItem && (
                  <div className="px-5 py-3 bg-indigo-50/50 border-b border-indigo-100">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] text-indigo-600 font-semibold uppercase tracking-wider">Selected</p>
                        <p className="text-sm font-semibold text-slate-900 truncate">{selectedItem.jobNumber}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button onClick={autoDetectRoof} disabled={autoDetectLoading} className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-300 disabled:cursor-wait text-white text-xs font-medium rounded-md transition-colors flex items-center gap-1 shadow-sm shadow-indigo-200">
                          <FiZap className="w-3 h-3" /> {autoDetectLoading ? 'Detecting…' : 'Auto Detect'}
                        </button>
                        <button onClick={startDrawingPolygon} className="px-2.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-md transition-colors flex items-center gap-1 border border-slate-200">
                          <FiHexagon className="w-3 h-3" /> Manual
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-auto px-5 py-4 space-y-2">
                  {(activeTab === 'leads' ? leadsLoading : projectsLoading) || geocodingLoading ? (
                    <div className="flex items-center justify-center py-12"><Spinner size="lg" /></div>
                  ) : paginatedItems.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <FiMapPin className="w-4 h-4 text-slate-400" />
                      </div>
                      <p className="text-sm text-slate-500">No {activeTab} found</p>
                    </div>
                  ) : (
                    paginatedItems.map((item) => <ItemCard key={item._id} item={item} type={activeTab} isSelected={selectedItem?._id === item._id} />)
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-white">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={`p-1.5 rounded-md transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600 hover:bg-slate-100'}`}>
                      <FiChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-xs text-slate-600 font-medium">Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className={`p-1.5 rounded-md transition-colors ${currentPage >= totalPages ? 'text-slate-300' : 'text-slate-600 hover:bg-slate-100'}`}>
                      <FiChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Totals Footer */}
          {measurements.length > 0 && (
            <div className="p-4 bg-white border-t border-slate-200">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-4 bg-indigo-500 rounded-full" />
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total Roof Area</span>
                </div>
                <button onClick={() => setShowReport(true)} className="text-[11px] bg-slate-50 hover:bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md flex items-center gap-1.5 font-medium border border-slate-200 transition-colors">
                  <FiFileText className="w-3 h-3" /> Report
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold mb-0.5">Actual</p>
                  <p className="text-xl font-semibold text-slate-900 tabular-nums">{totals.totalActual.toLocaleString(undefined, { maximumFractionDigits: 0 })}<span className="text-xs font-normal text-slate-500 ml-1">sq ft</span></p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <p className="text-indigo-600 text-[10px] uppercase tracking-wider font-semibold mb-0.5">Squares</p>
                  <p className="text-xl font-semibold text-indigo-700 tabular-nums">{totals.squares.toFixed(1)}<span className="text-xs font-normal text-indigo-500 ml-1">sq</span></p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 relative h-[50vh] md:h-full">
          <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-1 border border-slate-200">
            <div className="flex gap-0.5">
              {mapStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setMapStyle(style.id)}
                  title={style.name}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition-all ${
                    mapStyle === style.id
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {style.icon}<span className="hidden md:inline">{style.name}</span>
                </button>
              ))}
            </div>
          </div>


          {geocodingLoading && (
            <div className="absolute top-4 left-4 z-10 bg-white px-3.5 py-2 rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] flex items-center gap-2 border border-slate-200">
              <Spinner size="sm" /><span className="text-sm text-slate-600">Loading location…</span>
            </div>
          )}

          {/* Live measurement banner — visible while a draw is in progress */}
          {isDrawing && liveDraw && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-slate-200 flex items-center gap-3 pointer-events-none whitespace-nowrap">
              {liveDraw.kind === 'line' ? (
                <>
                  <span
                    className="inline-flex items-center justify-center w-1.5 h-5 rounded-sm"
                    style={{ backgroundColor: liveDraw.color || '#ef4444' }}
                  />
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Length</span>
                  <span className="text-base font-semibold text-slate-900 tabular-nums">{Math.round(liveDraw.totalFt).toLocaleString()}<span className="text-xs text-slate-500 ml-0.5">ft</span></span>
                  <span className="w-px h-4 bg-slate-200" />
                  <span className="text-[11px] text-slate-500">{liveDraw.segmentCount} segment{liveDraw.segmentCount === 1 ? '' : 's'}</span>
                </>
              ) : (
                <>
                  <span className="inline-flex w-1.5 h-5 rounded-sm bg-slate-400" />
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Flat</span>
                  <span className="text-base font-semibold text-slate-900 tabular-nums">{Math.round(liveDraw.flatSqFt).toLocaleString()}<span className="text-xs text-slate-500 ml-0.5">sf</span></span>
                  <span className="w-px h-4 bg-slate-200" />
                  <span className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">Actual</span>
                  <span className="text-base font-semibold text-indigo-700 tabular-nums">{Math.round(liveDraw.actualSqFt).toLocaleString()}<span className="text-xs text-indigo-500 ml-0.5">sf</span></span>
                  <span className="text-[10px] text-slate-400">@ {liveDraw.pitch}/12</span>
                  <span className="w-px h-4 bg-slate-200" />
                  <span className="text-[11px] text-slate-500">Perim {Math.round(liveDraw.perimeterFt)}'</span>
                </>
              )}
            </div>
          )}

          <div ref={mapContainer} className="w-full h-full" />

          {/* Floating Drawing Toolbar */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-1.5 border border-slate-200">
            <div className="flex items-center gap-0.5">
              {/* Auto Detect Roof */}
              <button 
                onClick={autoDetectRoof}
                disabled={autoDetectLoading}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg font-medium transition-all text-sm ${
                  autoDetectLoading
                    ? 'bg-indigo-100 text-indigo-400 cursor-wait'
                    : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm shadow-indigo-200'
                }`}
                title="Auto-detect building outline from satellite data"
              >
                {autoDetectLoading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <FiZap className="w-3.5 h-3.5" strokeWidth={2.5} />
                )}
                <span className="hidden sm:inline">{autoDetectLoading ? 'Detecting…' : 'Auto Detect'}</span>
              </button>

              {/* Divider */}
              <div className="w-px h-6 bg-slate-200 mx-1.5" />

              {/* Area Drawing */}
              <button 
                onClick={startDrawingPolygon}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg font-medium transition-all text-sm ${
                  isDrawing && drawingMode === 'polygon' 
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
                title="Draw roof outline"
              >
                <FiHexagon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Outline</span>
              </button>

              {/* Divider */}
              <div className="w-px h-6 bg-slate-200 mx-1.5" />

              {/* Line Drawing Tools */}
              {LINE_TYPES.slice(0, 6).map(lt => {
                const active = isDrawing && drawingMode === 'line' && selectedLineType === lt.id;
                return (
                  <button
                    key={lt.id}
                    onClick={() => startDrawingLine(lt.id)}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg font-medium transition-all text-xs"
                    style={{ 
                      backgroundColor: active ? lt.color : 'transparent',
                      color: active ? 'white' : '#475569',
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    title={lt.description}
                  >
                    <div 
                      className="w-3.5 h-[3px] rounded-sm" 
                      style={{ backgroundColor: active ? 'white' : lt.color }} 
                    />
                    <span className="hidden lg:inline">{lt.label}</span>
                  </button>
                );
              })}

              {/* More lines dropdown */}
              <div className="relative" ref={moreDropdownRef}>
                <button 
                  className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-all text-xs font-medium"
                  title="More line types"
                  onClick={() => setShowMoreDropdown((v) => !v)}
                >
                  <span>More</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showMoreDropdown && (
                  <div className="absolute bottom-full left-0 mb-2 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-1.5 flex flex-col gap-0.5 min-w-[140px]">
                      {LINE_TYPES.slice(6).map(lt => (
                        <button
                          key={lt.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            startDrawingLine(lt.id);
                            setShowMoreDropdown(false);
                          }}
                          className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium transition-colors hover:bg-slate-50 text-slate-700"
                          title={lt.description}
                        >
                          <div className="w-3.5 h-[3px] rounded-sm" style={{ backgroundColor: lt.color }} />
                          <span>{lt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-slate-200 mx-1.5" />

              {/* Undo & Clear */}
              <button 
                onClick={undoLastDrawing} 
                disabled={measurements.length === 0 && roofLines.length === 0}
                className={`p-2 rounded-lg transition-all ${
                  (measurements.length === 0 && roofLines.length === 0) 
                    ? 'text-slate-300 cursor-not-allowed' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="Undo last"
              >
                <FiRotateCcw className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={clearAllDrawings} 
                disabled={measurements.length === 0 && roofLines.length === 0}
                className={`p-2 rounded-lg transition-all ${
                  (measurements.length === 0 && roofLines.length === 0) 
                    ? 'text-slate-300 cursor-not-allowed' 
                    : 'text-red-500 hover:bg-red-50'
                }`}
                title="Clear all"
              >
                <FiTrash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            
            {/* Active tool indicator */}
            {isDrawing && (
              <div 
                className="absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md text-white text-[11px] font-medium whitespace-nowrap shadow-md"
                style={{ 
                  backgroundColor: drawingMode === 'line' 
                    ? LINE_TYPES.find(t => t.id === selectedLineType)?.color || '#6366f1'
                    : '#6366f1'
                }}
              >
                {drawingMode === 'polygon' ? 'Drawing Outline' : `Drawing ${LINE_TYPES.find(t => t.id === selectedLineType)?.label}`}
                <span className="ml-2 opacity-75">· Double-click to finish</span>
              </div>
            )}
          </div>
        </div>

        {/* Section Editor Modal */}
        {editingSection && <SectionEditor section={editingSection} onClose={() => setEditingSection(null)} />}

        {/* Line Editor Modal */}
        {editingLine && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Edit Line</h3>
                <button onClick={() => setEditingLine(null)} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors">
                  <FiX className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Line Type</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {LINE_TYPES.map(lt => {
                      const active = editingLine.type === lt.id;
                      return (
                        <button
                          key={lt.id}
                          onClick={() => setEditingLine({ ...editingLine, type: lt.id })}
                          className="flex flex-col items-center justify-center px-2 py-2.5 rounded-md text-xs font-medium transition-colors border"
                          style={{
                            backgroundColor: active ? lt.color : 'white',
                            color: active ? 'white' : '#475569',
                            borderColor: active ? lt.color : '#e2e8f0',
                          }}
                        >
                          <div className="w-full h-[3px] rounded-sm mb-1.5" style={{ backgroundColor: active ? 'white' : lt.color }} />
                          <span>{lt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-3 text-center">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mb-0.5">Length</p>
                  <p className="text-2xl font-semibold text-slate-900 tabular-nums">{Math.round(editingLine.lengthFt)}<span className="text-sm text-slate-500 ml-1">ft</span></p>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-slate-200 flex gap-2">
                <button 
                  onClick={() => setEditingLine(null)} 
                  className="flex-1 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => { updateLine(editingLine.id, { type: editingLine.type }); setEditingLine(null); }} 
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <FiCheck className="w-3.5 h-3.5" /> Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Dialog (replaces alert) */}
        {dialog && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => setDialog(null)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    dialog.tone === 'error' ? 'bg-red-50 text-red-600'
                    : dialog.tone === 'warning' ? 'bg-amber-50 text-amber-600'
                    : 'bg-indigo-50 text-indigo-600'
                  }`}>
                    <FiInfo className="w-4 h-4" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <h3 className="font-semibold text-slate-900 text-[15px] leading-tight">{dialog.title}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{dialog.message}</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setDialog(null)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
                  autoFocus
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Modal */}
        {showReport && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm shadow-indigo-200">JRT</div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-base tracking-tight">Roof Measurement Report</h3>
                    <p className="text-slate-500 text-xs mt-0.5">{currentAddress || 'Address not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={printReport} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg flex items-center gap-2 transition-colors text-sm font-medium">
                    <FiPrinter className="w-3.5 h-3.5" /><span>Print</span>
                  </button>
                  <button onClick={() => setShowReport(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500">
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto p-6 bg-slate-50/50">
                {/* Report Settings */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Customer Name</label>
                    <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Enter customer name" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Project Date</label>
                    <input type="date" value={projectDate} onChange={(e) => setProjectDate(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Waste Factor</label>
                    <select value={wasteFactor} onChange={(e) => setWasteFactor(parseFloat(e.target.value))} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100">
                      {WASTE_FACTORS.map(w => <option key={w.value} value={w.value}>{w.label} - {w.description}</option>)}
                    </select>
                  </div>
                </div>

                {/* Roof Diagram */}
                <div className="mb-6">
                  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <FiCompass className="w-3 h-3" />
                    Roof Diagram with Measurements
                  </h4>
                  <div className="flex justify-center bg-white rounded-xl p-4 border border-slate-200">
                    <RoofDiagram measurements={measurements} roofLines={roofLines} width={600} height={400} />
                  </div>
                </div>

                {/* Line Lengths Summary */}
                {roofLines.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Roof Line Measurements</h4>
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                      {LINE_TYPES.map(lt => {
                        const total = lineTotals[lt.id]?.totalFt || 0;
                        if (total === 0 && lt.id !== 'drip') return null;
                        return (
                          <div 
                            key={lt.id}
                            className="rounded-xl p-3 text-center bg-white border border-slate-200"
                          >
                            <div className="w-full h-0.5 rounded mb-2" style={{ backgroundColor: lt.color }} />
                            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: lt.color }}>{lt.label}</p>
                            <p className="text-lg font-semibold text-slate-900 tabular-nums">{Math.round(total)}</p>
                            <p className="text-[10px] text-slate-500">ft</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-indigo-600 text-white rounded-xl p-4 shadow-sm shadow-indigo-200">
                    <p className="text-indigo-200 text-[10px] uppercase tracking-wider font-semibold mb-1.5">Total Roof Area</p>
                    <p className="text-2xl font-semibold tabular-nums">{totals.totalActual.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    <p className="text-indigo-200 text-xs">sq ft</p>
                  </div>
                  <div className="bg-indigo-600 text-white rounded-xl p-4 shadow-sm shadow-indigo-200">
                    <p className="text-indigo-200 text-[10px] uppercase tracking-wider font-semibold mb-1.5">Roofing Squares</p>
                    <p className="text-2xl font-semibold tabular-nums">{totals.squares.toFixed(1)}</p>
                    <p className="text-indigo-200 text-xs">squares</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <p className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold mb-1.5">With {(wasteFactor * 100).toFixed(0)}% Waste</p>
                    <p className="text-2xl font-semibold text-slate-900 tabular-nums">{totals.withWaste.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    <p className="text-slate-500 text-xs">sq ft</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <p className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold mb-1.5">Squares w/ Waste</p>
                    <p className="text-2xl font-semibold text-slate-900 tabular-nums">{totals.squaresWithWaste.toFixed(1)}</p>
                    <p className="text-slate-500 text-xs">squares</p>
                  </div>
                </div>

                {/* Section Details Table */}
                <div className="mb-6">
                  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Section Details</h4>
                  <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Section</th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                          <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Pitch</th>
                          <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Flat Area</th>
                          <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Actual Area</th>
                          <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Perimeter</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {measurements.map(m => {
                          const sectionType = SECTION_TYPES.find(t => t.id === m.type);
                          return (
                            <tr key={m.id} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-4 py-2.5 font-medium text-slate-900 text-sm">{m.name}</td>
                              <td className="px-4 py-2.5">
                                <span className="px-2 py-0.5 rounded text-[11px] font-medium border" style={{ backgroundColor: `${sectionType?.color}15`, color: sectionType?.color, borderColor: `${sectionType?.color}30` }}>
                                  {sectionType?.label}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center text-slate-600 text-sm tabular-nums">{m.pitch}/12</td>
                              <td className="px-4 py-2.5 text-right text-slate-600 text-sm tabular-nums">{m.flatAreaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</td>
                              <td className="px-4 py-2.5 text-right font-semibold text-slate-900 text-sm tabular-nums">{m.areaSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</td>
                              <td className="px-4 py-2.5 text-right text-slate-600 text-sm tabular-nums">{m.perimeterFt.toFixed(0)} ft</td>
                            </tr>
                          );
                        })}
                        <tr className="bg-slate-50 font-semibold">
                          <td className="px-4 py-2.5 text-slate-700 text-sm" colSpan={3}>TOTAL</td>
                          <td className="px-4 py-2.5 text-right text-slate-700 text-sm tabular-nums">{totals.totalFlat.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</td>
                          <td className="px-4 py-2.5 text-right text-indigo-700 text-sm tabular-nums">{totals.totalActual.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</td>
                          <td className="px-4 py-2.5 text-right text-slate-700 text-sm tabular-nums">{totals.totalPerimeter.toFixed(0)} ft</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Waste Factor Table */}
                <div className="mb-6">
                  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Waste Factor Reference</h4>
                  <div className="grid grid-cols-7 gap-1.5">
                    {WASTE_FACTORS.map(w => {
                      const area = totals.totalActual * (1 + w.value);
                      const isSelected = w.value === wasteFactor;
                      return (
                        <div key={w.value} className={`px-2 py-2.5 rounded-lg text-center transition-colors ${isSelected ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200' : 'bg-white border border-slate-200'}`}>
                          <p className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-slate-900'}`}>{w.label}</p>
                          <p className={`text-[10px] tabular-nums ${isSelected ? 'text-indigo-200' : 'text-slate-500'}`}>{(area / 100).toFixed(1)} sq</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
                  <p className="font-semibold mb-2 text-slate-700">Notes</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-500 marker:text-slate-400">
                    <li>Measurements are calculated from satellite imagery and may vary from actual dimensions.</li>
                    <li>Actual roof area accounts for roof pitch using standard multipliers.</li>
                    <li>Always verify measurements on-site before ordering materials.</li>
                    <li>1 roofing square = 100 square feet.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RoofMeasurement;
